import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import * as Switch from '@radix-ui/react-switch'

import { pushPreferencesApi } from '@/api/endpoints'
import { useSuspiciousBeta } from '@/hooks/useSuspiciousBeta'
import type { PushCategory, PushFilters } from '@/types'
import { cn } from '@/lib/utils'
import { toast } from '@/lib/toast'
import { PushDeadZones } from './PushDeadZones'
import { SectionSkeleton, SectionError, SaveStatus } from './SectionState'
import { pushPreferencesQuery, pushCatalogQuery } from './prefetch'

const ORDER: PushCategory[] = ['animal', 'human', 'vehicle', 'audio', 'other']

/**
 * Categories that CANNOT be narrowed to individual classes.
 *
 * `other` covers motion-only captures, which carry NO detected classes at all — and it is
 * the largest bucket. Narrowing it would replace the category token with its class list
 * (COCO objects), and motion, having no class, would then match nothing: unticking a single
 * `chair` would silently kill every motion notification, with no checkbox to get it back.
 */
const NOT_NARROWABLE: ReadonlySet<PushCategory> = new Set<PushCategory>(['other'])

/**
 * #139 — per-account push notification categories, rewritten against the explicit
 * `PushFilters` contract. GET always returns all five categories with their real,
 * server-computed state (default: every category `enabled: true`, `classes: []`) —
 * this component seeds directly from that response and never re-derives a default.
 * That client-side inference ("empty selection means everything") was the original
 * bug: the picker showed every toggle OFF while the server notified about
 * everything, because "nothing selected" and "nothing wanted" were conflated.
 *
 * Per category:
 *   enabled + no classes ticked -> the whole category (future species included)
 *   enabled + classes ticked    -> only those classes, within that category
 *   disabled                    -> nothing from this category; its class list
 *                                  renders disabled so the nesting stays visible
 *
 * All categories off is a legitimate, saveable state (no push at all) — the
 * server accepts it (200). There is no "select at least one" block here.
 */
export function PushCategoriesSection() {
  const { t } = useTranslation('settings')
  const queryClient = useQueryClient()
  // #408 — the Suspicious switch is for beta users. Without beta it is not shown and not sent: the server keeps what it has.
  const beta = useSuspiciousBeta()

  const {
    data: prefs,
    isPending: prefsPending,
    isError: prefsFailed,
    refetch: refetchPrefs,
  } = useQuery(pushPreferencesQuery)
  const {
    data: catalog,
    isPending: catalogPending,
    isError: catalogFailed,
    refetch: refetchCatalog,
  } = useQuery(pushCatalogQuery)
  // Item 307 — a failed load shows this section's own "Couldn't load — Try again" (retrying what failed).
  const loadFailed = (prefsFailed && !prefs) || (catalogFailed && !catalog)
  const retryLoad = () => {
    if (prefsFailed) void refetchPrefs()
    if (catalogFailed) void refetchCatalog()
  }

  const [filters, setFilters] = useState<PushFilters | null>(null)
  // #406 — the account's Suspicious switch, next to the filters on GET and PUT; independent of the categories. null =
  // an answer without it (an older API): reads as on, and nothing is sent unless the person moves it.
  const [suspicious, setSuspicious] = useState<boolean | null>(null)
  const [dirty, setDirty] = useState(false)

  // Re-sync when the server data lands or changes — but NEVER over unsaved edits. A
  // background refetch (another tab saving, a reconnect) would otherwise wipe what the
  // user is editing and take the Save button away with it.
  const dirtyRef = useRef(dirty)
  dirtyRef.current = dirty
  useEffect(() => {
    if (!prefs || dirtyRef.current) return
    // `filters` holds the five categories and nothing else, also when an older answer stored the switch among them
    const { suspicious: storedSwitch, ...categories } = prefs.filters
    setFilters(categories as PushFilters)
    setSuspicious(prefs.suspicious ?? storedSwitch ?? null)
    setDirty(false)
  }, [prefs])

  const save = useMutation({
    mutationFn: () => {
      if (!filters) throw new Error('push preferences not loaded yet')
      return pushPreferencesApi.update(!beta || suspicious === null ? { filters } : { filters, suspicious })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pushPreferencesQuery.queryKey })
      setDirty(false)
      toast.success(t('pushCategories.saved'))
    },
    onError: () => toast.error(t('pushCategories.saveFailed')),
  })

  const classesFor = (cat: PushCategory): string[] =>
    catalog?.groups.find((g) => g.category === cat)?.class_names ?? []

  const toggleCategory = (cat: PushCategory) => {
    if (!filters) return
    setFilters({
      ...filters,
      [cat]: { ...filters[cat], enabled: !filters[cat].enabled },
    })
    setDirty(true)
  }

  const toggleClass = (cat: PushCategory, cls: string) => {
    if (!filters) return
    const current = filters[cat]
    const nextClasses = current.classes.includes(cls)
      ? current.classes.filter((c) => c !== cls)
      : [...current.classes, cls]
    setFilters({ ...filters, [cat]: { ...current, classes: nextClasses } })
    setDirty(true)
  }

  const suspiciousOn = suspicious !== false
  const toggleSuspicious = (on: boolean) => {
    setSuspicious(on)
    setDirty(true)
  }

  const allOff = filters ? ORDER.every((cat) => !filters[cat].enabled) : false

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">{t('pushCategories.title')}</h2>
        <p className="text-sm text-muted-foreground">{t('pushCategories.description')}</p>
      </div>

      {loadFailed ? (
        <SectionError onRetry={retryLoad} />
      ) : (prefsPending || catalogPending) && (
        <SectionSkeleton rows={ORDER.length} />
      )}

      {allOff && (
        <p
          role="alert"
          className="rounded-md bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400"
        >
          {t(beta && suspiciousOn ? 'pushCategories.allOffSuspiciousOn' : 'pushCategories.allOffWarning')}
        </p>
      )}

      {filters && catalog && (
        <div className="divide-y divide-border rounded-lg border border-border">
          {ORDER.map((cat) => {
            const catState = filters[cat]
            const classes = classesFor(cat)
            const label = t(`pushCategories.categories.${cat}`)
            const switchId = `push-category-${cat}`

            return (
              <div key={cat} className="p-3">
                <div className="flex items-center gap-3">
                  <Switch.Root
                    id={switchId}
                    checked={catState.enabled}
                    onCheckedChange={() => toggleCategory(cat)}
                    aria-label={label}
                    className={cn(
                      'relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      'data-[state=checked]:bg-emerald-500 data-[state=unchecked]:bg-muted-foreground/30',
                    )}
                  >
                    <Switch.Thumb className="pointer-events-none block h-3.5 w-3.5 rounded-full bg-white shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0.5" />
                  </Switch.Root>

                  <label htmlFor={switchId} className="min-w-0 flex-1 truncate text-sm">
                    {label}
                  </label>
                </div>

                {/* The class list is ALWAYS rendered when the catalog has classes for this
                    category (except NOT_NARROWABLE ones, see above) — the category/class
                    nesting must be visible, not implied by a hidden "customize" toggle. When
                    the category is off, the same list stays on screen but disabled, so it is
                    obvious those classes are inert. */}
                {classes.length > 0 && !NOT_NARROWABLE.has(cat) && (
                  <div className="mt-3 pl-12">
                    <p className="mb-2 text-xs text-muted-foreground">
                      {!catState.enabled
                        ? t('pushCategories.categoryOffHint')
                        : catState.classes.length === 0
                          ? t('pushCategories.allClassesHint')
                          : null}
                    </p>
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                      {classes.map((cls) => (
                        <label
                          key={cls}
                          className={cn(
                            'flex items-center gap-2 text-xs',
                            !catState.enabled && 'opacity-50',
                          )}
                        >
                          <input
                            type="checkbox"
                            checked={catState.classes.includes(cls)}
                            onChange={() => toggleClass(cat, cls)}
                            disabled={!catState.enabled}
                            className="h-3.5 w-3.5 shrink-0 rounded border-border"
                          />
                          <span className="truncate" title={cls}>
                            {cls}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
          {beta && (
            <div className="flex items-start gap-3 p-3">
              <Switch.Root
                id="push-category-suspicious"
                checked={suspiciousOn}
                onCheckedChange={toggleSuspicious}
                aria-labelledby="push-category-suspicious-label"
                aria-describedby="push-category-suspicious-legend"
                className={cn(
                  'relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  'data-[state=checked]:bg-emerald-500 data-[state=unchecked]:bg-muted-foreground/30',
                )}
              >
                <Switch.Thumb className="pointer-events-none block h-3.5 w-3.5 rounded-full bg-white shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0.5" />
              </Switch.Root>
              <div className="min-w-0 flex-1">
                <label id="push-category-suspicious-label" htmlFor="push-category-suspicious" className="text-sm">
                  {t('myPush.suspiciousSwitch')}
                </label>
                <p id="push-category-suspicious-legend" className="text-xs text-muted-foreground">{t('myPush.suspiciousLegend')}</p>
              </div>
            </div>
          )}
        </div>
      )}

      <p className="text-xs text-muted-foreground">{t('pushCategories.muteHint')}</p>

      {filters && (
        <PushDeadZones filters={filters} />
      )}

      {(dirty || save.isSuccess) && (
        <div className="flex items-center gap-3">
          {dirty && (
            <button
              type="button"
              onClick={() => save.mutate()}
              disabled={save.isPending}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              {t('pushCategories.save')}
            </button>
          )}
          <SaveStatus pending={save.isPending} saved={save.isSuccess && !dirty} />
        </div>
      )}
    </section>
  )
}
